import { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../layout/Header';
import AikupCard from '../components/AikupCard';
import AikupModal from '../components/AikupModal';
import FormationGestionDocumentaireCard from '../components/FormationGestionDocumentaireCard';
import ArbrePRIsMECard from '../components/ArbrePRIsMECard';
import { gestionnairesData, type GestionnaireItem } from '../data/gestionnairesData';

export default function Gestionnaires() {
  const [selectedItem, setSelectedItem] = useState<GestionnaireItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCardClick = (item: GestionnaireItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedItem(null), 300);
  };

  // Organiser par catégorie
  const categories = Array.from(new Set(gestionnairesData.map(item => item.category)));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header 
        title="Gestionnaires" 
        subtitle="Outils et frameworks pour piloter ton équipe avec excellence. KPI, gouvernance, leadership, performance - tout ce dont tu as besoin."
      />

      <div className="max-w-7xl mx-auto px-8 pb-12">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500"
          >
            <p className="text-sm text-gray-600 mb-1">Outils Disponibles</p>
            <p className="text-3xl font-bold text-gray-900">{gestionnairesData.length}</p>
            <p className="text-xs text-gray-500 mt-1">Frameworks pratiques</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500"
          >
            <p className="text-sm text-gray-600 mb-1">Score Maturité</p>
            <p className="text-3xl font-bold text-gray-900">
              {Math.round(gestionnairesData.reduce((sum, item) => sum + item.kpiScore, 0) / gestionnairesData.length)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">Moyenne excellence</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500"
          >
            <p className="text-sm text-gray-600 mb-1">Catégories</p>
            <p className="text-3xl font-bold text-gray-900">{categories.length}</p>
            <p className="text-xs text-gray-500 mt-1">Domaines couverts</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500"
          >
            <p className="text-sm text-gray-600 mb-1">Priorité Leadership</p>
            <p className="text-3xl font-bold text-gray-900">
              {gestionnairesData.filter(item => item.category === 'Leadership').length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Outils leadership</p>
          </motion.div>
        </div>

        {/* Cards par catégorie */}
        {categories.map((category, categoryIndex) => {
          const itemsInCategory = gestionnairesData.filter(item => item.category === category);
          
          return (
            <div key={category} className="mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="flex-1 h-px bg-gradient-to-r from-blue-500 to-purple-500"></div>
                <h2 className="text-2xl font-bold text-gray-800 px-4">{category}</h2>
                <div className="flex-1 h-px bg-gradient-to-l from-blue-500 to-purple-500"></div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {itemsInCategory.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: categoryIndex * 0.1 + idx * 0.05 }}
                  >
                    <AikupCard
                      item={item}
                      onClick={() => handleCardClick(item)}
                      onExpertClick={() => {}}
                      index={idx}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Cards Formation et Arbre PRIsME */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <FormationGestionDocumentaireCard
            department="Gestionnaires"
            domain="Gestionnaires"
            index={0}
          />
          <ArbrePRIsMECard
            index={1}
          />
        </div>

        {/* Message motivation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-2xl p-8 text-white"
        >
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-3xl">
                💪
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-3">Leadership d'Excellence</h3>
              <p className="text-blue-100 text-lg leading-relaxed mb-4">
                Ces outils ne sont pas de la théorie - c'est du <strong>pragmatisme pur</strong>. 
                Chaque framework a été testé, validé, et génère des résultats mesurables.
              </p>
              <p className="text-blue-100 text-lg leading-relaxed mb-4">
                Un gestionnaire sans outils, c'est comme un menuisier sans scie. 
                Tu travailles 2x plus fort pour des résultats 2x moins bons.
              </p>
              <p className="text-white text-xl font-semibold">
                ⚡ Commence par <strong>Dashboard + RACI + 1-on-1</strong> - le trio gagnant.
              </p>
              <p className="text-blue-200 text-sm mt-3">
                ROI moyen de ces outils: +35% productivité équipe, +40% rétention, -60% firefighting.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modal */}
      {selectedItem && (
        <AikupModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          item={selectedItem}
        />
      )}
    </div>
  );
}

